/**
 * Cliente del API (patrón de SGDEA/Planning): la sesión es una cookie httpOnly; cada petición manda los segundos de
 * inactividad. Los errores llegan como ApiError {status, code, message, details}.
 */
import { IDLE_HEADER } from '@suite/contracts';
import { idleSeconds } from '@/app/activity';

let unauthorized: ((code: string) => void) | null = null;
export function onUnauthorized(fn: (code: string) => void) {
  unauthorized = fn;
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string, public readonly details?: unknown, public readonly correlationId?: string) {
    super(message);
  }
}

export const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

type Query = Record<string, string | number | boolean | null | undefined | string[]>;

export async function api<T>(path: string, init: RequestInit & { json?: unknown; query?: Query; base?: string } = {}): Promise<T> {
  const { json, query, headers, base, ...rest } = init;
  let url = `${base ?? API_BASE}${path}`;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v)) v.forEach((x) => qs.append(k, x));
      else qs.set(k, String(v));
    }
    const q = qs.toString();
    if (q) url += `?${q}`;
  }
  const res = await fetch(url, {
    ...rest,
    credentials: 'include',
    headers: { [IDLE_HEADER]: String(idleSeconds()), ...(json !== undefined ? { 'content-type': 'application/json' } : {}), ...(headers ?? {}) },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const e = (data ?? {}) as { code?: string; message?: string; details?: unknown; correlationId?: string };
    if (res.status === 401 && !path.startsWith('/auth/') && !base) unauthorized?.(e.code ?? 'UNAUTHENTICATED');
    throw new ApiError(res.status, e.code ?? 'HTTP_ERROR', e.message ?? `Error ${res.status}`, e.details, e.correlationId);
  }
  return data as T;
}

export const apiGet = <T>(path: string, query?: Query) => api<T>(path, { query });
export const apiPost = <T>(path: string, json?: unknown) => api<T>(path, { method: 'POST', json });
export const apiPut = <T>(path: string, json?: unknown) => api<T>(path, { method: 'PUT', json });
export const apiPatch = <T>(path: string, json?: unknown) => api<T>(path, { method: 'PATCH', json });
export const apiDelete = <T>(path: string) => api<T>(path, { method: 'DELETE' });

export const errorMessage = (e: unknown) => (e instanceof ApiError ? e.message : 'Algo salió mal. Intenta de nuevo.');
